import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { testimonials } from "@/data";
import { JSX } from "react/jsx-runtime";
import { InView } from "@/lib/animations/components";
import { staggerOrchestrator, cardItem, rowItem } from "@/lib/animations/variants";


export function TestimonialsSection(): JSX.Element {
  const featured = testimonials.filter((t) => t.featured);
  const secondary = testimonials.filter((t) => !t.featured);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        <InView className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-[#1A1A1A] mb-4">
            Trusted by Leading Businesses
          </h2>
          <p className="text-[#6B6B6B] max-w-lg mx-auto">
            Don't just take our word for it. Here's what our clients say about working with Rosewood.
          </p>
        </InView>

        <motion.div
          variants={staggerOrchestrator(0.1, 0.05)}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          {featured.map((testimonial) => (
            <motion.div
              key={testimonial.id}
              variants={cardItem}
              className="bg-[#FAF7F2] rounded-2xl p-7 transition-all relative"
            >
              <Quote size={32} className="text-[#C9A84C]/30 absolute top-6 right-6" />

              <div className="flex gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Star key={i} size={14} fill="#C9A84C" className="text-[#C9A84C]" />
                ))}
              </div>

              <p className="text-[#1A1A1A] leading-relaxed mb-6 text-sm">
                "{testimonial.content}"
              </p>

              <div className="inline-block px-3 py-1 bg-[#6B1E2A]/8 rounded-full text-xs font-medium text-[#6B1E2A] mb-5">
                {testimonial.service}
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#6B1E2A] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">
                    {testimonial.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A] text-sm">{testimonial.name}</p>
                  <p className="text-xs text-[#6B6B6B]">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={staggerOrchestrator(0.06, 0.05)}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-40px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {secondary.map((testimonial) => (
            <motion.div
              key={testimonial.id}
              variants={rowItem}
              className="bg-white rounded-xl p-5 flex items-start gap-4"
            >
              <div className="w-9 h-9 bg-[#6B1E2A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#6B1E2A] font-semibold text-xs">
                  {testimonial.name.split(" ").map((n) => n[0]).join("")}
                </span>
              </div>
              <div>
                <div className="flex gap-1 mb-1.5">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} size={11} fill="#C9A84C" className="text-[#C9A84C]" />
                  ))}
                </div>
                <p className="text-xs text-[#6B6B6B] mb-2 line-clamp-2">
                  "{testimonial.content}"
                </p>
                <p className="text-xs font-semibold text-[#1A1A1A]">{testimonial.name}</p>
                <p className="text-[11px] text-[#6B6B6B]">{testimonial.company}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
}